import { useState, useEffect } from 'react';
import { Edit2, X, Loader, Sparkles, Check, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VIBES     = ['Comfort','Light & Fresh','Indulgent','Celebrate','Quick Bite','Date Night','Casual','Energizing'];
const OCCASIONS = ['Brunch','Lunch','Dinner','Birthday','Anniversary','Work Lunch','Family Meal','Solo Treat','Date Night'];

const EditPostModal = ({ post, onClose, onUpdated }) => {
  const { user }          = useAuth();
  const [caption, setCaption]   = useState(post.caption || '');
  const [tags, setTags]         = useState((post.tags || []).join(', '));
  const [vibe, setVibe]         = useState(post.vibe || '');
  const [occasion, setOccasion] = useState(post.occasion || '');
  const [notes, setNotes]       = useState(post.notes || '');
  const [saving, setSaving]     = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError]       = useState('');

  const generateCaption = async () => {
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/posts/generate-caption`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ venue_name: post.venue_name, location: post.location, cuisine: post.cuisine, vibe, occasion, notes }),
      });
      if (res.ok) {
        const d = await res.json();
        setCaption(d.caption || caption);
        if (d.tags?.length) setTags(d.tags.join(', '));
      }
    } finally { setAiLoading(false); }
  };

  const handleSave = async () => {
    if (!caption.trim()) { setError('Caption is required'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/posts/${post.post_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ caption, tags, vibe, occasion, notes }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Save failed'); }
      const updated = await res.json();
      onUpdated?.(updated);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Edit2 className="w-4 h-4" />
            <h2 className="font-bold text-base">Edit Post</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Post preview */}
          {post.image_url && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl">
              <img src={post.image_url} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" onError={e => e.target.style.display='none'} />
              <div className="min-w-0">
                {post.venue_name && <p className="text-xs font-semibold text-gray-700 truncate">{post.venue_name}</p>}
                <p className="text-[10px] text-gray-400">{post.cuisine || 'Post'}</p>
              </div>
            </div>
          )}

          {/* Caption */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-semibold text-gray-700">Caption</label>
              <button onClick={generateCaption} disabled={aiLoading}
                className="flex items-center gap-1 text-xs text-[#FF6B35] font-medium hover:underline disabled:opacity-50">
                {aiLoading ? <Loader className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Regenerate
              </button>
            </div>
            <textarea value={caption} onChange={e => setCaption(e.target.value)} rows={3} maxLength={500}
              className="w-full text-sm border border-gray-200 rounded-2xl px-3 py-2.5 focus:outline-none focus:border-[#FF6B35] resize-none" />
            <p className="text-xs text-gray-400 text-right">{caption.length}/500</p>
          </div>

          {/* Tags */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block flex items-center gap-1">
              <Tag className="w-3 h-3 text-[#FF6B35]" />Tags
            </label>
            <input value={tags} onChange={e => setTags(e.target.value)} placeholder="ramen, latenight, cozy..."
              className="w-full text-sm border border-gray-200 rounded-2xl px-3 py-2 focus:outline-none focus:border-[#FF6B35]" />
          </div>

          {/* Vibe chips */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Vibe</label>
            <div className="flex flex-wrap gap-1.5">
              {VIBES.map(v => (
                <button key={v} onClick={() => setVibe(vibe === v ? '' : v)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-all ${vibe === v ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'border-gray-200 text-gray-600 hover:border-[#FF6B35]'}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Occasion chips */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Occasion</label>
            <div className="flex flex-wrap gap-1.5">
              {OCCASIONS.map(o => (
                <button key={o} onClick={() => setOccasion(occasion === o ? '' : o)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-all ${occasion === o ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'border-gray-200 text-gray-600 hover:border-[#FF6B35]'}`}>
                  {o}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Notes</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="What did you order?"
              className="w-full text-sm border border-gray-200 rounded-2xl px-3 py-2 focus:outline-none focus:border-[#FF6B35]" />
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-2xl">{error}</p>}

          <div className="flex gap-3 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-2xl">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white rounded-2xl">
              {saving ? <><Loader className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Check className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPostModal;
